import React from 'react';

import { NullModel } from 'types/ai_model';

import { AppEntity, AppEntityObject } from 'hooks/useEntities';
import { objectHas } from 'services/object';

import { AdvancedSearchContext } from './services/AdvancedSearchContext';
import { filterList } from './services/filter';
import { ENTITY_RESOLVER, useSuggestions } from './hooks/useSuggestions';
import {
  AdvancedFilterDispatchAction,
  AdvancedFilterOptions,
  AdvancedFilterState,
  AdvancedSearchRelatedEntityFilterItem,
  AdvancedSearchRelatedEntityFilterList,
} from './AdvancedSearch.types';
import {
  AdvancedSearchFilter as AdvancedSearchFilterCmp,
  AdvancedSearchFilterPublicProps,
} from './components/AdvancedSearchFilter/AdvancedSearchFilter';

const NULL_ENTITY_RESOLVER: EntityMap = {
  models: new NullModel(),
};

type EntityMap = {
  [entity in AppEntity]?: any;
};

export type AdvancedSearchProviderProps<T> = React.PropsWithChildren<{
  data: T[];
  isLoading?: boolean;
  entity: AppEntity;
  filterOptions?: AdvancedFilterOptions<T>;
  onFilteredDataChange?: React.Dispatch<React.SetStateAction<number>>;
  onSearchInputChange?: (searchInput: string) => void;
  searchOnInputChange?: boolean;
}>;

/** @deprecated */
export function AdvancedSearchProvider<T extends AppEntityObject>({
  data,
  isLoading,
  entity,
  filterOptions,
  onFilteredDataChange,
  onSearchInputChange,
  searchOnInputChange,
  children,
}: AdvancedSearchProviderProps<T>) {
  const baseEntity = NULL_ENTITY_RESOLVER[entity] as T;

  const [isSearching, setIsSearching] = React.useState(false);
  const [filteredData, setFilteredData] = React.useState<T[]>(data);

  /** User input in search field to control suggestions */
  const [searchInput, setSearchInput] = React.useState<string>('');

  /** Plain-text search directly in top-level list */
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  /** Entity filters applied to data */
  const [relatedEntityFilters, setRelatedEntityFilters] = React.useState<
    AdvancedSearchRelatedEntityFilterItem<T>[]
  >([]);

  const filterRef = React.useRef<AdvancedFilterState>({});
  const [filters, dispatchFilters] = React.useReducer(filterReducer, {});
  const [filterTracker, forceFilter] = React.useReducer(() => {
    filterRef.current = filters;
    return {};
  }, {});

  const { suggestions, findSuggestions, suggestionsCount } = useSuggestions<T>(
    baseEntity,
    relatedEntityFilters,
    data
  );
  const suggestionTimeout = React.useRef<number>();

  React.useEffect(() => setIsSearching(true), [searchInput]);

  React.useEffect(() => {
    onSearchInputChange?.(searchInput);
  }, [onSearchInputChange, searchInput]);

  React.useEffect(() => {
    if (searchOnInputChange) {
      setSearchTerm(searchInput.trim());
    }
  }, [searchInput, searchOnInputChange]);

  React.useEffect(() => {
    window.clearTimeout(suggestionTimeout.current);

    suggestionTimeout.current = window.setTimeout(() => {
      if (searchInput && searchInput.trim().length >= 2) {
        findSuggestions(searchInput);
        setSearchInput(searchInput);
        setIsSearching(false);
      } else {
        findSuggestions();
        setIsSearching(false);
      }
    }, 200);

    return () => {
      window.clearTimeout(suggestionTimeout.current);
    };
  }, [searchInput, findSuggestions]);

  React.useEffect(() => {
    const filterResults = filterList(baseEntity, data, relatedEntityFilters, {
      searchTerm,
      filters: filterRef.current,
    }) as T[];
    setFilteredData(filterResults);
  }, [baseEntity, data, relatedEntityFilters, searchTerm, filterTracker]);

  React.useEffect(() => {
    onFilteredDataChange?.(filteredData.length);
  }, [filteredData, onFilteredDataChange]);

  const handleSearch = React.useCallback(() => {
    setSearchTerm(searchInput.trim());
  }, [searchInput]);

  const handleFilter = React.useCallback(
    (
      property: keyof T,
      filter: AdvancedSearchRelatedEntityFilterItem<T>['filter']
    ) => {
      setRelatedEntityFilters((previous) => {
        const newState = [...previous];
        newState.push({ property, filter });

        return newState;
      });
      setSearchInput('');
    },
    []
  );

  const handleRemoveFilter = React.useCallback((property?: keyof T) => {
    if (!property) {
      setRelatedEntityFilters((previous) => previous.slice(0, -1));
    } else {
      setRelatedEntityFilters((previous) => {
        const filterIndex = previous.findIndex(
          (filter) => filter.property === property
        );
        if (filterIndex === -1) return previous;
        const newState = [...previous];
        newState.splice(filterIndex, 1);
        return newState;
      });
    }
  }, []);

  const getPropertyFromEntityName = React.useCallback(
    (entity: AppEntity) => {
      return Object.keys(ENTITY_RESOLVER).find(
        (property) =>
          ENTITY_RESOLVER[property] === entity &&
          objectHas(baseEntity, property)
      ) as keyof AppEntityObject;
    },
    [baseEntity]
  );

  const AdvancedSearchFilter = React.useCallback(
    (props: AdvancedSearchFilterPublicProps) => {
      return <AdvancedSearchFilterCmp {...props} />;
    },
    []
  );

  return (
    <AdvancedSearchContext.Provider
      value={{
        data,
        filteredData,
        isLoading,
        isSearching,
        isEmpty: !data?.length,
        suggestions,
        suggestionsCount,
        searchInput,
        setSearchInput,
        handleSearch,

        relatedEntityFilters:
          relatedEntityFilters as AdvancedSearchRelatedEntityFilterList<AppEntityObject>,
        setRelatedEntityFilter: handleFilter,
        removeRelatedEntityFilter: handleRemoveFilter,
        getPropertyFromEntityName,

        filters,
        dispatchFilters,
        handleApplyFilters: forceFilter,
        filterOptions: filterOptions as AdvancedFilterOptions<AppEntityObject>,
        AdvancedSearchFilter,
      }}
    >
      {children}
    </AdvancedSearchContext.Provider>
  );
}

function filterReducer(
  state: AdvancedFilterState,
  action: AdvancedFilterDispatchAction
) {
  const value = action.value === undefined ? true : action.value;

  return {
    ...state,
    [action.property]: {
      ...state[action.property],
      [action.key]: value,
    },
  };
}
